/**
 * The one place that knows how the server says "stop offering this passkey".
 *
 * Every login refusal carries the same client-safe message, deliberately: the
 * nine reasons the backend can refuse are collapsed so that nothing about an
 * account leaks to a caller who has not authenticated. What the browser still
 * needs from that flat answer is one bit — will retrying ever work? A credential
 * this backend has no record of can never succeed, so the device should forget
 * it rather than run the ceremony again on every visit; everything else is a
 * moment passing and the passkey must survive it.
 */

/**
 * Error extension code for a credential this backend will never accept.
 *
 * One code for every terminal reason. Which of them fired is exactly what the
 * flat message exists to hide, and the reasons that turn on an account property
 * — MFA, staff policy — deliberately do not carry it: the credential lookup runs
 * before the signature is checked, so anything distinguishable there answers
 * "is this credential's owner staff?" for a caller holding no key at all.
 */
export const PASSKEY_CREDENTIAL_UNUSABLE = 'PASSKEY_CREDENTIAL_UNUSABLE';

/** A login refusal, and whether the credential is worth keeping. */
export class PasskeyRefusedError extends Error {
    /** The credential can never authenticate here; the device should forget it. */
    readonly terminal: boolean;

    constructor(message: string, terminal: boolean) {
        super(message);
        this.name = 'PasskeyRefusedError';
        this.terminal = terminal;
    }
}

/**
 * Whether an error says the credential itself is finished.
 *
 * Anything that is not a refusal we recognise answers no. A dismissed prompt, a
 * network fault or a response shaped by an older server all reach here, and
 * treating any of them as terminal would delete a working passkey.
 */
export const isTerminalRefusal = (err: unknown): boolean =>
    err instanceof PasskeyRefusedError && err.terminal;

/**
 * Turn the verify action's failure into the error the step throws.
 *
 * `terminal` is absent against a server that predates the code, so it is read as
 * false rather than defaulted the other way: the cost of being wrong here is a
 * passkey destroyed on a deploy skew, against one slower login for being
 * cautious.
 */
export const refusalFromVerify = (failure: {
    message: string;
    terminal?: boolean;
}): PasskeyRefusedError =>
    new PasskeyRefusedError(failure.message, failure.terminal === true);
